import { BevoegdGezagOutput, DeelnemerOutput, DeskundigeOutput, ToelichtingsTypeEnum, WebGebruikerGroep, WebGebruikerInput } from '@/lib/types';
import { MilieudienstOutput } from '@/lib/types/milieudienst';
import { Checkbox, Divider, Form, Input, Select } from 'antd';
import { Store } from 'antd/es/form/interface';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { constants } from '@/lib/constants';
import { Roles, useUserContext } from '@/lib/context/userContext';

type WebGebruikerGroepOption = {
  value: WebGebruikerGroep;
  label: string;
  disabled?: boolean;
};

export interface WebGebruikerFormProps {
  onFinish: (values: WebGebruikerInput) => void | undefined;
  milieudiensten: MilieudienstOutput[];
  deskundigen: DeskundigeOutput[];
  bevoegdGezagen: BevoegdGezagOutput[];
  deelnemers: DeelnemerOutput[];
  initialValues?: Store;
  id?: string;
  initialWebGebruikerGroep?: WebGebruikerGroep;
  isCreate: boolean;
  readOnly?: boolean;
}
export const WebGebruikerForm: React.FunctionComponent<WebGebruikerFormProps> = ({
  onFinish,
  initialWebGebruikerGroep,
  milieudiensten,
  deskundigen,
  bevoegdGezagen,
  deelnemers,
  initialValues,
  id,
  isCreate,
  readOnly = false,
}) => {
  const [webGebruikerGroep, setWebGebruikerGroep] = useState(initialWebGebruikerGroep);
  const { t } = useTranslation('core');
  const [form] = Form.useForm();
  const { roles } = useUserContext();
  const formItemLayout = { labelCol: { span: 6 }, wrapperCol: { span: 14 } };
  const webGebruikerGroepOptions: WebGebruikerGroepOption[] = Object.values(WebGebruikerGroep)
    .filter((value): value is WebGebruikerGroep => typeof value === 'number')
    .filter(item => item !== WebGebruikerGroep.Toezichthouder)
    .map(item => {
      return { value: item, label: WebGebruikerGroep[item] };
    });

  if (initialWebGebruikerGroep === WebGebruikerGroep.Toezichthouder) {
    webGebruikerGroepOptions.unshift({
      value: WebGebruikerGroep.Toezichthouder,
      label: 'Toezichthouder (NIET gebruiken!)',
      disabled: true,
    });
  }

  const isAdmin = (): boolean => {
    return roles.includes(Roles.Administrator);
  };

  return (
    <Form
      id={id}
      className="form oneColumn"
      {...formItemLayout}
      layout={'horizontal'}
      form={form}
      initialValues={initialValues}
      onFinish={onFinish}
      disabled={readOnly}>
      <Form.Item label={t('webGebruiker.loginNaam')} name="loginNaam" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('webGebruiker.email')} name="email" rules={[{ required: true, type: 'email' }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('webGebruiker.contactpersoon')} name="contactpersoon" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('webGebruiker.functieContactpersoon')} name="functieContactpersoon" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('webGebruiker.ondertekenaar')} name="ondertekenaar" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('webGebruiker.functieOndertekenaar')} name="functieOndertekenaar" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('webGebruiker.webGebruikerGroep')} name="webGebruikerGroepId" rules={[{ required: true }]}>
        <Select onChange={setWebGebruikerGroep} options={webGebruikerGroepOptions} />
      </Form.Item>

      {(webGebruikerGroep === WebGebruikerGroep.Gemeente || webGebruikerGroep === WebGebruikerGroep.Waterschap) && (
        <Form.Item label={t('webGebruiker.bevoegdGezag')} name="bevoegdgezagId" rules={[{ required: true }]}>
          <Select>
            {bevoegdGezagen
              .filter(x => x.bevoegdGezagTypeId === (webGebruikerGroep === WebGebruikerGroep.Gemeente ? constants.gemeenteRolId : constants.milieudienstRolId))
              .map(item => (
                <Select.Option key={item.id} value={item.id}>
                  {item.volledigeNaam}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      )}

      {webGebruikerGroep === WebGebruikerGroep.Deelnemer && (
        <Form.Item label={t('webGebruiker.deelnemer')} name="deelnemerId" rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={deelnemers.map(item => {
              return { value: item.id, label: item.uoNummer };
            })}
          />
        </Form.Item>
      )}
      {webGebruikerGroep === WebGebruikerGroep.Milieudienst && (
        <Form.Item label={t('webGebruiker.milieudienst')} name="milieudienstId" rules={[{ required: true }]}>
          <Select
            options={milieudiensten.map(item => {
              return { value: item.id, label: item.volledigeNaam };
            })}
          />
        </Form.Item>
      )}
      {webGebruikerGroep === WebGebruikerGroep.Deskundige && (
        <Form.Item label={t('webGebruiker.deskundige')} name="deskundigeId" rules={[{ required: true }]}>
          <Select
            options={deskundigen.map(item => {
              return { value: item.id, label: item.bedrijfsNaam };
            })}
          />
        </Form.Item>
      )}
      {(webGebruikerGroep === WebGebruikerGroep.Gemeente ||
        webGebruikerGroep === WebGebruikerGroep.Waterschap ||
        webGebruikerGroep === WebGebruikerGroep.Milieudienst) && (
        <Form.Item label={t('webGebruiker.keyuser')} name="keyUser" valuePropName="checked">
          <Checkbox disabled={!isAdmin()}></Checkbox>
        </Form.Item>
      )}
      {(webGebruikerGroep === WebGebruikerGroep.Gemeente ||
        webGebruikerGroep === WebGebruikerGroep.Waterschap ||
        webGebruikerGroep === WebGebruikerGroep.Milieudienst ||
        webGebruikerGroep === WebGebruikerGroep.Administrator) && (
        <Form.Item label={t('webGebruiker.kvkNummer')} name="kvkNummer" rules={[{ required: false }]}>
          <Input minLength={8} maxLength={8} />
        </Form.Item>
      )}
      {!isCreate && (
        <>
          <Divider>{t('webGebruiker.toelichtingOpWijzigingen')}</Divider>
          <br />
          <Form.Item
            label={t('webGebruiker.toelichtingsTypeWijziging')}
            name="toelichtingOpWijzigingType"
            initialValue={ToelichtingsTypeEnum.Onbekend}
            rules={[{ required: false }]}>
            <Select
              disabled={true}
              options={Object.values(ToelichtingsTypeEnum)
                .filter(v => !Number.isNaN(Number(v)))
                .map(item => {
                  return { value: item, label: ToelichtingsTypeEnum[item] };
                })}
            />
          </Form.Item>
          <Form.Item label={t('webGebruiker.toelichting')} name="toelichtingOpWijziging" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </>
      )}
    </Form>
  );
};
